import Cookies from 'js-cookie';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Redirect, Route } from 'react-router-dom';
import styled from 'styled-components';
import { createTranslator } from '../language/index';
import { Loading } from '../other/Player-github/style';
import * as axios from './axios';
import { initDb } from './local-db';
import { setInfo } from './user_info';

const authContext = createContext();

export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export const useAuth = () => {
  return useContext(authContext);
};

export function useProvideAuth() {
  const [auth, setAuth] = useState(() => {
    // Verificar se já existe autenticação salva ao inicializar
    const username = Cookies.get('username');
    const password = Cookies.get('password');
    if (username && password) {
      // Tentar login automático se houver credenciais
      return null; // null indica que ainda está verificando
    }
    return 0;
  });
  const [t, setT] = useState(() => (k, p) => k);

  const getLang = () => {
    try {
      const s = localStorage.getItem('lang');
      if (s) return s;
    } catch (_) {}
    if (typeof window !== 'undefined' && window.lang) return window.lang;
    return 'pt-BR';
  };

  useEffect(() => {
    let mounted = true;
    createTranslator(getLang()).then(({ t }) => {
      if (mounted) setT(() => t);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Verificar autenticação ao montar o componente
  useEffect(() => {
    let mounted = true;
    const username = Cookies.get('username');
    const password = Cookies.get('password');

    // Se houver credenciais salvas, tentar login automático
    if (username && password) {
      const dns =
        (!window.dns || window.dns.length === 0) && Cookies.get('dns');
      if (dns) axios.setDns(dns);

      axios
        .post('player_api.php', {
          username: username.trim(),
          password: password.trim(),
        })
        .then((result) => {
          if (!mounted) return;

          if (result) {
            if (result.data) result = result.data;
            else if (result.response && result.response.data)
              result = result.response.data;
          }
          if (result && result.user_info) {
            if (result.iptveditor) {
              const iptveditorApi =
                typeof window !== 'undefined' && window.REACT_APP_IPTVEDITOR_API
                  ? window.REACT_APP_IPTVEDITOR_API
                  : process.env.REACT_APP_IPTVEDITOR_API || '';
              if (iptveditorApi) {
                axios.setDns(`${iptveditorApi}webplayer`);
              }
            }
            if (result.user_info.auth === 0) {
              setAuth(0);
            } else if (result.user_info.auth) {
              if (result.user_info.status === 'Active') {
                setAuth(1);
                setInfo(result.user_info, result.server_info);
                initDb();
              } else {
                setAuth(0);
              }
            }
          } else {
            setAuth(0);
          }
        })
        .catch(() => {
          if (mounted) setAuth(0);
        });
    } else {
      // Se não houver credenciais, garantir que auth seja 0
      if (mounted) setAuth(0);
    }

    return () => {
      mounted = false;
    };
  }, []);

  const signin = (dns, username, password, successFallback, failFallback) => {
    if (dns) axios.setDns(dns);

    axios
      .post('player_api.php', {
        username: username.trim(),
        password: password.trim(),
      })
      .then((result) => {
        if (result) {
          if (result.data) result = result.data;
          else if (result.response && result.response.data)
            result = result.response.data;
        }
        if (result && result.user_info) {
          if (result.iptveditor) {
            const iptveditorApi =
              typeof window !== 'undefined' && window.REACT_APP_IPTVEDITOR_API
                ? window.REACT_APP_IPTVEDITOR_API
                : process.env.REACT_APP_IPTVEDITOR_API || '';
            if (iptveditorApi) {
              axios.setDns(`${iptveditorApi}webplayer`);
            }
          }
          if (result.user_info.auth === 0)
            failFallback(t('auth.noAccountTitle'), t('auth.noAccountDesc'));
          else if (result.user_info.auth) {
            if (result.user_info.status !== 'Active')
              failFallback(
                t('auth.expiredTitle'),
                t('auth.expiredDesc', {
                  date: new Date(
                    parseInt(result.user_info.exp_date + '000')
                  ).toGMTString(),
                })
              );
            else {
              setAuth(1);
              setInfo(result.user_info, result.server_info);
              initDb();
              successFallback && successFallback();
            }
          }
        } else if (result.title) {
          failFallback && failFallback(result.title, result.body);
        } else {
          failFallback &&
            failFallback(t('auth.serverErrTitle'), t('auth.serverErrDesc2'));
        }
      })
      .catch((err) => {
        console.log(err);
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.user_info &&
          err.response.data.user_info.auth === 0
        )
          failFallback &&
            failFallback(t('auth.noAccountTitle'), t('auth.noAccountDesc'));
        else
          failFallback &&
            failFallback(t('auth.serverErrTitle'), t('auth.serverErrDesc1'));
      });
  };

  const authLogin = (fallback) => {
    const dns = (!window.dns || window.dns.length === 0) && Cookies.get('dns');
    const username = Cookies.get('username');
    const password = Cookies.get('password');

    if (username && password) signin(dns, username, password, fallback);
  };

  const signout = (action) => {
    setAuth(null);
    action && action();
  };

  const isAuth = () => {
    // Só retorna true se realmente estiver autenticado (auth === 1)
    // Se auth for null, ainda está verificando - retorna false para mostrar login
    // Se auth for 0, não está autenticado
    return auth === 1;
  };

  const isChecking = () => {
    // Retorna true se ainda está verificando autenticação
    return auth === null;
  };

  return {
    signin,
    signout,
    isAuth,
    authLogin,
    isChecking,
  };
}

const LoadingContainer = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--first-color);
`;

export function PrivateRoute({ children, ...rest }) {
  let auth = useAuth();
  return (
    <Route
      {...rest}
      render={({ location }) => {
        // Se ainda está verificando, mostrar loading
        if (auth.isChecking && auth.isChecking()) {
          return (
            <LoadingContainer>
              <Loading color={'var(--second-color)'}>
                <div>
                  <div />
                  <div />
                  <div />
                </div>
              </Loading>
            </LoadingContainer>
          );
        }
        // Se autenticado, mostrar conteúdo
        if (auth.isAuth()) {
          return children;
        }
        // Se não autenticado, redirecionar para login
        return (
          <Redirect
            to={{
              pathname: '/login/',
              state: { from: location },
            }}
          />
        );
      }}
    />
  );
}
